import React, { useRef, useEffect, useState } from 'react';
import styles from './styles.scss';

import Popup, { PopupProps } from '../Popup';
import RichTextInput from '../RichTextInput';
import { NoteSanitizedHTMLRender } from '../Note';

import {
  AppBar,
  AppBarSection,
  AppBarTitle,
  AppBarContext,
  Button,
  Icons,
} from '@densityco/ui';
import HorizontalForm from '../HorizontalForm';

type AddNoteFormProps = {
  placeholder: string;
  submitDisabled?: boolean;
  submitLabel?: string;
  onSubmit: () => void;

  value: string;
  onChangeValue: (value: string) => void;

  controls?: React.ReactNode;
  buttons?: React.ReactNode;
  width?: React.ReactText;
};

const AddNoteForm = React.forwardRef(
  (
    {
      placeholder,
      submitDisabled,
      submitLabel,
      onSubmit,
      value,
      onChangeValue,
      controls = null,
      buttons = null,
      width = 600,
    }: AddNoteFormProps,
    ref,
  ) => {
    const [preview, setPreview] = useState(false);
    return (
      <div style={{ width }}>
        {preview ? (
          <div className={styles.preview}>
            <NoteSanitizedHTMLRender body={value} />
          </div>
        ) : (
          <RichTextInput
            width="100%"
            value={value}
            onChange={v => onChangeValue(v)}
            placeholder={placeholder}
            ref={ref}
          />
        )}

        {/* Optionally render some controls underneath the main editor */}
        {!preview && controls ? (
          <div className={styles.controls}>
            {controls}
          </div>
        ) : null}

        <AppBar>
          <AppBarSection>
            <Button onClick={() => setPreview(!preview)}>
              {preview ? 'Continue Editing' : 'Preview'}
            </Button>
          </AppBarSection>
          <AppBarSection>
            <HorizontalForm>
              {buttons}
              <Button
                disabled={submitDisabled}
                variant="filled"
                onClick={() => onSubmit()}
                data-test-id="add-update-note-submit-button"
              >
                {submitLabel || 'Add Note'}
              </Button>
            </HorizontalForm>
          </AppBarSection>
        </AppBar>
      </div>
    );
  },
);

export default AddNoteForm;

type AddNotePopupProps = AddNoteFormProps & {
  title?: React.ReactNode;

  preFormContent?: React.ReactNode;

  buttonLabel: React.ReactNode;
  buttonVariant?: React.ComponentProps<typeof Button>['variant'];

  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  position?: PopupProps['position'];
};

export const AddNotePopup: React.FunctionComponent<AddNotePopupProps> = ({
  title=null,
  buttonLabel,
  buttonVariant,
  preFormContent,
  open,
  onOpen,
  onClose,
  position = { right: 0, top: 44 },
  ...formProps
}) => {
  const editor = useRef<any>(null);
  useEffect(() => {
    if (open) {
      editor.current.focus();
    }
  }, [open]);

  return (
    <Popup
      target={
        <Button variant={buttonVariant} data-test-id="add-note-popup-target">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {buttonLabel}
            &nbsp;&nbsp;
            <Icons.ChevronDown />
          </div>
        </Button>
      }
      open={open}
      onOpen={() => onOpen()}
      onClose={() => onClose()}
      position={position}
      noPadding
    >
      {title ? (
        <AppBarContext.Provider value="CARD_HEADER">
          <AppBar>
            <AppBarTitle>{title}</AppBarTitle>
          </AppBar>
        </AppBarContext.Provider>
      ) : null}

      {/* Add optional context to the form explaining what submitting it will do */}
      {preFormContent ? (
        <div className={styles.preFormContent}>
          {preFormContent}
        </div>
      ) : null}

      <AddNoteForm
        ref={editor}
        buttons={<Button variant="underline" onClick={() => onClose()}>Cancel</Button>}
        {...formProps}
      />
    </Popup>
  );
};
